import React from "react";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import "./ServicePackageModal.css";
import { useTranslation, Trans } from "react-i18next";

export default function ServicePackageModal({ show, onHide, vehicle }) {
  const { t } = useTranslation();
  const service = vehicle?.servicePackages;

  if (!service) return null;

  const variants = service.variants || [];
  const mileageOptions = service.mileageOptions || [];
  const prices = service.prices || {};

  const validationSchema = Yup.object().shape({
    variant: Yup.string()
      .required(t("servicePackageModal.validation.selectVariant"))
      .notOneOf([""], t("servicePackageModal.validation.selectVariant")),
    mileage: Yup.string()
      .required(t("servicePackageModal.validation.selectMileage"))
      .notOneOf([""], t("servicePackageModal.validation.selectMileage")),
  });

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="brand-modal-header"></Modal.Header>

      <Formik
        initialValues={{ variant: "", mileage: "" }}
        validationSchema={validationSchema}
        onSubmit={() => {}}
      >
        {({
          values,
          handleChange,
          handleBlur,
          errors,
          touched,
          setFieldValue,
        }) => {
          const variantSelected = Boolean(values.variant);
          const mileageSelected = Boolean(values.mileage);

          const price =
            variantSelected && mileageSelected
              ? prices?.[values.variant]?.[values.mileage] || "—"
              : null;

          const selectedVariantTableRows = variantSelected
            ? mileageOptions.map((m) => ({
                mileage: m,
                price: prices?.[values.variant]?.[m] || "—",
              }))
            : [];

          return (
            <Form>
              <Modal.Body>
                <h4 className="servicePackageModal-title">
                  {t("servicePackageModal.title")}
                </h4>

                <Row className="align-items-center mb-3">
                  {vehicle?.imageSrc && (
                    <img
                      src={vehicle.imageSrc}
                      alt={t(vehicle.titleKey)}
                      className="service-modal__vehicle-image"
                    />
                  )}

                  <div className="text-center">
                    <h5 className="service-modal__vehicle-title">
                      {t(vehicle.titleKey)}
                    </h5>

                    <p className="servicePackage-modal__inclusive-text">
                      {t("servicePackageModal.inclusiveVat")}
                    </p>
                  </div>

                  <Row className="g-3 mt-1">
                    {/* Variant */}
                    <Col xs={12} md={6}>
                      <Form.Group controlId="variant">
                        <Form.Label className="brand-input-label">
                          {t("servicePackageModal.selectVariantLabel")}
                        </Form.Label>

                        <Form.Select
                          className="brand-input"
                          name="variant"
                          value={values.variant}
                          onChange={(e) => {
                            handleChange(e);
                            // Reset mileage when variant changes
                            setFieldValue("mileage", "");
                          }}
                          onBlur={handleBlur}
                        >
                          <option value="">
                            {t("servicePackageModal.selectVariantPlaceholder")}
                          </option>
                          {variants.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </Form.Select>

                        {errors.variant && touched.variant && (
                          <div className="service-modal__error">
                            {errors.variant}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* Mileage */}
                    <Col xs={12} md={6}>
                      <Form.Group controlId="mileage">
                        <Form.Label className="brand-input-label">
                          {t("servicePackageModal.selectMileageLabel")}
                        </Form.Label>

                        <Form.Select
                          className="brand-input"
                          name="mileage"
                          value={values.mileage}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={!variantSelected}
                        >
                          <option value="">
                            {t("servicePackageModal.selectMileagePlaceholder")}
                          </option>
                          {mileageOptions.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </Form.Select>

                        {errors.mileage && touched.mileage && (
                          <div className="service-modal__error">
                            {errors.mileage}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Row>

                {/* ✅ Price Line: show only when both selected (with real <strong> rendering) */}
                {variantSelected && mileageSelected && (
                  <div className="service-modal__price-line">
                    <Trans
                      i18nKey="servicePackageModal.priceLine"
                      values={{
                        variant: values.variant,
                        mileage: values.mileage,
                        price: price,
                      }}
                      components={{ strong: <strong /> }}
                    />
                  </div>
                )}

                {/* ✅ Table */}
                {variantSelected && mileageSelected && (
                  <div className="service-modal__table-wrap">
                    <Table responsive bordered hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>{t("servicePackageModal.table.mileage")}</th>
                          <th>{t("servicePackageModal.table.servicePrice")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedVariantTableRows.map((row) => (
                          <tr key={row.mileage}>
                            <td>{row.mileage}</td>
                            <td>{row.price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Modal.Body>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}